// WebTransport の接続 (ADR-0001)。制御は双方向ストリーム 1 本、音声は Datagram。
//
// server/README.md「接続の流れ」のとおり:
//   1. https://<host>:<port> に接続
//   2. クライアントが双方向ストリームを 1 本開く
//   3. list_rooms を送ると部屋の一覧 (名前と設定) が rooms で返る ← 入室前に送れる
//   4. join を送り、joined が返ったら音声フレームを Datagram で送る
//   5. leave を送るか接続を閉じれば退室
//
// UDP 遮断時のフォールバック (WebSocket) を足すときは、このファイルと同じ形の
// 別クラスを作り、main.ts で切り替える (語彙 control.ts はそのまま)。

import { type ClientMessage, Decoder, encodeMessage, PROTOCOL_VERSION, type ServerMessage } from "./control.ts";
import type { Stage } from "./reachability.ts";

/**
 * ハンドシェイクの時間切れ (ミリ秒)。
 *
 * **入れないとブラウザが数十秒待つ**ので、利用者には「固まった」としか見えない。
 * 打ち切って理由を出すほうが親切で、**かつ `timeout` が到達確認 (R1) の主信号になる**
 * (docs/design/0005-本番でWebSocketフォールバックと到達確認をどう実装するか.md §4-A)。
 * UDP が捨てられている回線では応答そのものが返らないので、ここで初めて分かる。
 */
export const HANDSHAKE_TIMEOUT_MS = 8000;

/** 段階が分かっている失敗。到達確認がそのまま `st` として送る。 */
export class TransportError extends Error {
  constructor(
    message: string,
    readonly stage: Stage,
    options?: { cause?: unknown },
  ) {
    super(message, options);
    this.name = "TransportError";
  }
}

export interface ConnectOptions {
  /** `https://host:port` */
  url: string;
  /** ハンドシェイクの時間切れ。既定は {@link HANDSHAKE_TIMEOUT_MS}。 */
  timeoutMs?: number;
}

export interface TransportEvents {
  onMessage: (message: ServerMessage) => void;
  onDatagram: (bytes: Uint8Array) => void;
  onClose: (reason: string) => void;
}

/**
 * 入室要求に添える省略可能な項目（**バージョンは上がらない**）。
 *
 * `room_display_name` は**畳まれたチャンネルへの入り直し**に使う（ADR-0022 §1-4）。
 * ほかの 3 つはサーバのログで追うための手掛かり（ADR-0012）。
 */
export interface JoinExtra {
  room_display_name?: string;
  app_version?: string;
  net_kind?: "wifi" | "cellular" | "other";
  rejoin_count?: number;
}

export class Transport {
  /**
   * 閉じたことを既に伝えたか。
   *
   * 閉じると **`wt.closed` と制御ストリームの読み取りの両方が終わる**ので、
   * そのまま流すと同じことを 2 回伝えてしまう（画面のログに「退室しました」が
   * 2 行出ていた。2026-10-01 の実測ログ）。最初の 1 回だけ伝える。
   */
  private closeNotified = false;

  /**
   * **`join` を送ったか**（#56 ②）。
   *
   * サーバは**入室前のメッセージを手順違反として扱う**（`server/common` の
   * `session.rs`）。接続しただけで入室していない状態でページを閉じると
   * `pagehide` から `leave()` が走り、サーバのログに WARN が出ていた
   * （2026-10-06 の試験で 5 件）。**入室していなければ `leave` は送らず、
   * 接続を閉じるだけ**にする（閉じれば退室と同じ扱いになる）。
   */
  private joinSent = false;

  private constructor(
    private readonly wt: WebTransport,
    private readonly control: WritableStreamDefaultWriter<Uint8Array>,
    private readonly datagrams: WritableStreamDefaultWriter<Uint8Array>,
  ) {}

  private notifyClose(events: TransportEvents, reason: string): void {
    if (this.closeNotified) return;
    this.closeNotified = true;
    events.onClose(reason);
  }

  /**
   * 接続して制御ストリームを開くところまで。**join はまだ送らない**。
   *
   * 部屋の設定 (コーデック・フレーム長) は入室応答で決まるので、先に
   * {@link listRooms} で一覧を見せて選ばせてから {@link join} する。
   */
  static async connect(options: ConnectOptions, events: TransportEvents): Promise<Transport> {
    if (typeof WebTransport === "undefined") {
      throw new TransportError("このブラウザは WebTransport に対応していない", "unsupported");
    }
    // 証明書は公的な CA のもの 1 組だけを使う (DOC-0019 §5)。ブラウザは通常どおり検証する
    const wt = new WebTransport(options.url);
    const timeoutMs = options.timeoutMs ?? HANDSHAKE_TIMEOUT_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        wt.ready,
        new Promise<never>((_, reject) => {
          timer = setTimeout(
            () =>
              reject(
                new TransportError(
                  `${timeoutMs} ms 待っても応答が無かった（UDP が通っていない可能性が高い）`,
                  "timeout",
                ),
              ),
            timeoutMs,
          );
        }),
      ]);
    } catch (e) {
      // 時間切れでも WebTransport は生きたまま待ち続けるので、必ず閉じる。
      // 既に壊れている接続を閉じると投げることがあるので、ここでは握り潰す
      // (握り潰さないと、元の失敗の理由が close の例外で上書きされる)
      try {
        wt.close();
      } catch {
        // 閉じられなければそのままでよい (ページを離れれば解放される)
      }
      throw e;
    } finally {
      clearTimeout(timer);
    }

    const stream = await wt.createBidirectionalStream();
    const control = stream.writable.getWriter();

    // 音声は Datagram で送る (ADR-0001)。送信口の取り方がブラウザで 2 通りある:
    //   - 新しい仕様: datagrams.createWritable()  ← Safari 26.4+ はこちらしか持たない
    //   - 以前の形  : datagrams.writable          ← 非推奨。Chrome などはまだこちら
    // 受信は readable のままでよい (置き換えは無い)。
    // 2026-09-24 に iPhone (iOS 26.6.2) で writable が undefined になり入室できなかった。
    const duplex = wt.datagrams as
      | (WebTransportDatagramDuplexStream & {
          createWritable?: () => WritableStream<Uint8Array>;
        })
      | undefined;
    const outgoing =
      typeof duplex?.createWritable === "function" ? duplex.createWritable() : duplex?.writable;
    if (!outgoing || !duplex?.readable) {
      void wt.close();
      throw new TransportError(
        "このブラウザの WebTransport は Datagram を送受信できないため入室できない" +
          `（音声に必要）。判別用: ${navigator.userAgent}`,
        "unsupported",
      );
    }
    const datagrams = outgoing.getWriter();
    const transport = new Transport(wt, control, datagrams);

    // 受信ループ 2 本は待たずに走らせる
    void transport.readControl(stream.readable, events);
    void transport.readDatagrams(events);
    wt.closed
      .then((info) =>
        transport.notifyClose(events, `接続が閉じた (code ${info.closeCode}: ${info.reason || "-"})`),
      )
      .catch((e: unknown) => transport.notifyClose(events, `接続が切れた: ${String(e)}`));

    return transport;
  }

  /**
   * 部屋の一覧を求める。応答 (rooms) は onMessage で届く。**入室前に送れる**。
   *
   * `group` を入れると**そのグループのチャンネルだけ**、省くと**常設の部屋だけ**返る
   * （ADR-0019 §1-9）。**変化したときはサーバが同じ `rooms` を押し出す**（ADR-0023）。
   */
  async listRooms(group?: string, watch = false): Promise<void> {
    const message: ClientMessage = { type: "list_rooms" };
    if (group) message.group = group;
    // **見張りを頼むと、変化のたびにサーバが `rooms` を押し出す**（ADR-0023）。
    // 省略可能な項目なので、古いサーバには黙って無視される（バージョンは上がらない）
    if (watch) message.watch = true;
    await this.control.write(encodeMessage(message));
  }

  /**
   * **チャンネルを作る**（ADR-0022 §1-3）。応答 (channel_created) は onMessage で届く。
   *
   * 利用者が決めるのは表示名だけで、識別子はサーバが採番する。**入室前に送れる。**
   */
  async createChannel(group: string, displayName: string): Promise<void> {
    await this.control.write(
      encodeMessage({ type: "create_channel", group, display_name: displayName }),
    );
  }

  /**
   * **新しいグループを作り、その中に最初のチャンネルを作る**（ADR-0024）。
   *
   * **グループのコードはサーバが採番する**（ADR-0021 の 50 ビットを保つため。同 §1-3 を上書き）。
   * 応答は `channel_created` で、`room` が `採番されたグループ/管理ID` になる。
   */
  async createGroup(displayName: string): Promise<void> {
    await this.control.write(
      encodeMessage({ type: "create_channel", group: "", display_name: displayName, new_group: true }),
    );
  }

  /** 入室する。応答 (joined) は onMessage で届く。 */
  async join(room: string, name: string, extra: JoinExtra = {}): Promise<void> {
    await this.control.write(
      encodeMessage({ type: "join", protocol_version: PROTOCOL_VERSION, room, name, ...extra }),
    );
    // 入室が**断られた**場合もここは true になるが、そのときサーバは接続を閉じた
    // 状態なので、あとから leave が届いても黙って捨てられる（手順違反にはならない）
    this.joinSent = true;
  }

  private async readControl(readable: ReadableStream<Uint8Array>, events: TransportEvents): Promise<void> {
    const reader = readable.getReader();
    const decoder = new Decoder();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        decoder.push(value);
        for (let m = decoder.next(); m !== null; m = decoder.next()) {
          events.onMessage(m);
        }
      }
    } catch (e) {
      this.notifyClose(events, `制御ストリームの異常: ${String(e)}`);
    }
  }

  private async readDatagrams(events: TransportEvents): Promise<void> {
    const reader = this.wt.datagrams.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        events.onDatagram(value);
      }
    } catch {
      // 接続断は wt.closed 側で通知する
    }
  }

  /**
   * **送信を試みた Datagram と、失敗した Datagram の累計**（2026-10-07 追加）。
   *
   * WebTransport の Datagram は**送信キューが埋まっていると黙って捨てられる**。
   * 一方 `client/core` の送信側は書き込みの成否に関わらず順序番号を進めるので、
   * **捨てられた分はサーバには「欠落」として見える**（回線に出ていないので NIC にも
   * UDP の計数器にも痕跡が残らない）。
   *
   * 2026-10-06 の試験で上り 2.4% の欠落が説明できず、VM の UDP 受信バッファ
   * （溢れ 18 / 297,411 = 0.006%）と NIC（`missed 0`）では足りなかった。
   * **ここが原因かを判定するために数える**（タスク #54）。
   */
  sent = 0;
  sendFailures = 0;

  /** 音声フレームを Datagram 1 つとして送る。送れなくても待たない (音は捨ててよい)。 */
  sendFrame(bytes: Uint8Array): void {
    this.sent += 1;
    this.datagrams.write(bytes).catch(() => {
      // 接続断は wt.closed 側で通知する。ここでは**数えるだけ**にして、
      // 「送れなかった」と「回線で落ちた」を切り分けられるようにする
      this.sendFailures += 1;
    });
  }

  /**
   * **合成音声での、ある参加者の音量を変える**（MCU だけ。#58）。
   *
   * MCU は合成された 1 本しか送ってこないので、混ざった後から 1 人を取り出せない。
   * サーバが混ぜるときに掛けてもらう。**掛かるのは自分に届く音だけ**で、
   * 相手が送るものも他の人が聞く音も変わらない。
   *
   * SFU の部屋で送るとサーバが `invalid_request` を返す（接続は切れない）。
   */
  sendSetMixGain(target: number, gainDb: number): void {
    // 音は捨ててよいのと同じで、1 つ送れなくても待たない（次のつまみ操作で上書きされる）
    void this.control
      .write(encodeMessage({ type: "set_mix_gain", target, gain_db: Math.round(gainDb) }))
      .catch(() => {
        // 接続断は wt.closed 側で通知する
      });
  }

  /**
   * leave を送って閉じる。**入室前は `leave` を送らず閉じるだけ**（#56 ②）。
   *
   * サーバは入室前のメッセージを手順違反として扱うので、送ると WARN が出る。
   * 接続を閉じれば退室と同じなので、送る必要もない。
   */
  async leave(): Promise<void> {
    try {
      if (this.joinSent) await this.control.write(encodeMessage({ type: "leave" }));
      await this.control.close();
    } catch {
      // すでに閉じていれば無視
    }
    this.wt.close({ closeCode: 0, reason: "leave" });
  }
}
